import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, KeyRound, LogOut, Mail, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MitgliedSuche from '@/components/MitgliedSuche';
import { coachVerwaltungAPI } from '@/lib/api';

/* Trainer-Zugänge zur Webseite /coach (10.10.2026). Overviewer sehen alle Athleten mit Kraftlog oder
   eigenem Plan, Personal Trainer nur die hier zugeordneten Kunden. Recht: trainingsplaner.edit —
   ohne das Recht bleibt die Karte unsichtbar (403 vom Server). */

const sel = 'flex h-9 rounded-md border border-input bg-background px-2 text-sm';
const zeit = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'noch nie');

function Zugang({ t, onGeaendert }) {
  const [wahl, setWahl] = useState(null);
  const [busy, setBusy] = useState(false);
  const aendern = async (daten, ok) => {
    setBusy(true);
    try { await coachVerwaltungAPI.aendern(t.id, daten); if (ok) toast.success(ok); onGeaendert(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
    setBusy(false);
  };
  const einladen = async () => {
    setBusy(true);
    try { const r = await coachVerwaltungAPI.einladen(t.id); r.data.gesendet ? toast.success(`Einladung an ${t.email} gesendet`) : toast.error('E-Mail konnte nicht gesendet werden – Link bitte selbst schicken'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Einladung fehlgeschlagen'); }
    setBusy(false);
  };
  const abmelden = async () => {
    try { const r = await coachVerwaltungAPI.abmelden(t.id); toast.success(`${r.data.beendet} Sitzung(en) beendet`); onGeaendert(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
  };
  const ids = t.kunden.map((k) => k.id);
  return (
    <li className="rounded-lg border border-border p-3 space-y-2" data-testid={`trainer-zugang-${t.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium break-words">{t.name}</span>
        <span className="text-xs text-muted-foreground break-all">{t.email}</span>
        {t.lead_id && <Badge variant="secondary" className="text-[10px]">meldet sich mit P1-App an</Badge>}
        {!t.aktiv && <Badge variant="outline" className="text-[10px]">deaktiviert</Badge>}
        <span className="ml-auto text-[11px] text-muted-foreground">zuletzt angemeldet: {zeit(t.zuletzt_angemeldet)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor={`rolle-${t.id}`}>Rolle</label>
        <select id={`rolle-${t.id}`} className={sel} value={t.rolle} disabled={busy}
          onChange={(e) => aendern({ rolle: e.target.value }, 'Rolle geändert – der Trainer muss sich neu anmelden')}>
          <option value="overviewer">Overviewer (sieht alle)</option>
          <option value="pt">Trainer / PT (eigene Zyklen und Kunden)</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm" title="Ernährungscoaching auf /coach: Dashboard, Pläne, Check-ins (Gesundheitsdaten — nur bewusst freischalten)">
          <input type="checkbox" checked={!!t.ernaehrung || !!t.master} disabled={busy || !!t.master}
            onChange={(e) => aendern({ ernaehrung: e.target.checked }, e.target.checked ? 'Ernährung freigeschaltet' : 'Ernährung gesperrt')} />
          Ernährung{t.master ? ' (Master: immer)' : ''}
        </label>
        <Button size="sm" variant="outline" className="gap-1.5" disabled={busy || !t.aktiv} onClick={einladen}><Mail className="w-3.5 h-3.5" /> Einladen</Button>
        <Button size="sm" variant="ghost" className="gap-1.5" onClick={abmelden}><LogOut className="w-3.5 h-3.5" /> Überall abmelden</Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => aendern({ aktiv: !t.aktiv }, t.aktiv ? 'Zugang deaktiviert' : 'Zugang aktiviert')}>
          {t.aktiv ? 'Deaktivieren' : 'Aktivieren'}
        </Button>
      </div>
      {t.rolle === 'pt' && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">Kunden ({t.kunden.length})</p>
          <div className="flex flex-wrap gap-1.5">
            {t.kunden.map((k) => (
              <span key={k.id} className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs">
                {k.name || 'gelöschte Person'}
                <button type="button" aria-label={`${k.name} entfernen`} disabled={busy} onClick={() => aendern({ kunden_ids: ids.filter((i) => i !== k.id) })}
                  className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <MitgliedSuche wert={wahl} placeholder="Kunden hinzufügen…" tid={`trainer-kunde-${t.id}`}
            onWahl={(w) => { if (w && !ids.includes(w.id)) aendern({ kunden_ids: [...ids, w.id] }, `${w.name} zugeordnet`); setWahl(null); }} />
        </div>
      )}
    </li>
  );
}

export default function TrainerZugaenge() {
  const [daten, setDaten] = useState(null);
  const [neu, setNeu] = useState(null);
  const [busy, setBusy] = useState(false);
  const lade = useCallback(async () => {
    try { setDaten((await coachVerwaltungAPI.liste()).data); }
    catch (e) { setDaten(e?.response?.status === 403 ? 'kein_recht' : 'fehler'); }
  }, []);
  useEffect(() => { lade(); }, [lade]);
  if (daten === null || daten === 'kein_recht') return null;
  if (daten === 'fehler') return null;

  const anlegen = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await coachVerwaltungAPI.anlegen(neu); toast.success('Zugang angelegt – jetzt „Einladen“ klicken'); setNeu(null); lade(); }
    catch (x) { toast.error(x?.response?.data?.detail || 'Nicht angelegt'); }
    setBusy(false);
  };
  const kopieren = async () => {
    try { await navigator.clipboard.writeText(daten.link); toast.success('Link kopiert'); } catch { toast.error(daten.link); }
  };

  return (
    <Card className="print:hidden" data-testid="trainer-zugaenge">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex flex-wrap items-center gap-2"><KeyRound className="w-4 h-4" /> Trainer-Zugänge (Kraftlog-Webseite)
          <button type="button" onClick={kopieren} className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground">
            <Copy className="w-3.5 h-3.5" /> {daten.link}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-muted-foreground">
          Trainer der P1-App melden sich unter dem Link mit ihren App-Zugangsdaten an (E-Mail + PIN) — ihr Zugang entsteht beim ersten Mal von selbst: Leitung und Headcoach als Overviewer, alle anderen sehen ihre eigenen Zyklen. Externe Trainer ohne App-Konto hier anlegen; sie melden sich per E-Mail-Code an. Overviewer sehen alle Athleten und Programme; Trainer/PT ihre eigenen Zyklen und die hier zugeordneten Kunden. Jede Änderung trägt den Namen des Trainers.
        </p>
        {daten.trainer.length > 0 && <ul className="space-y-2">{daten.trainer.map((t) => <Zugang key={t.id} t={t} onGeaendert={lade} />)}</ul>}
        {neu ? (
          <form onSubmit={anlegen} className="grid sm:grid-cols-4 gap-2 items-end">
            <div className="space-y-1"><Label htmlFor="tz-name">Name</Label><Input id="tz-name" className="h-9" required value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} /></div>
            <div className="space-y-1 sm:col-span-2"><Label htmlFor="tz-email">E-Mail</Label><Input id="tz-email" type="email" className="h-9" required value={neu.email} onChange={(e) => setNeu({ ...neu, email: e.target.value })} /></div>
            <div className="space-y-1"><Label htmlFor="tz-rolle">Rolle</Label>
              <select id="tz-rolle" className={`${sel} w-full`} value={neu.rolle} onChange={(e) => setNeu({ ...neu, rolle: e.target.value })}>
                <option value="overviewer">Overviewer</option><option value="pt">Trainer / PT</option>
              </select></div>
            <div className="flex gap-2 sm:col-span-4">
              <Button size="sm" type="submit" disabled={busy} data-testid="trainer-zugang-anlegen">Anlegen</Button>
              <Button size="sm" type="button" variant="ghost" onClick={() => setNeu(null)}>Abbrechen</Button>
            </div>
          </form>
        ) : (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setNeu({ name: '', email: '', rolle: 'overviewer' })} data-testid="trainer-zugang-neu"><Plus className="w-3.5 h-3.5" /> Trainer-Zugang anlegen</Button>
        )}
      </CardContent>
    </Card>
  );
}
